import { ApolloClient, InMemoryCache, HttpLink, split, ApolloLink } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

interface CreateApolloClientOptions {
  httpUrl: string;
  wsUrl: string;
  getSessionId: () => string;
  companyPublicId: string;
}

export function createApolloClient({ httpUrl, wsUrl, getSessionId, companyPublicId }: CreateApolloClientOptions) {
  /**
   * Create HTTP link for queries and mutations
   */
  const httpLink = new HttpLink({
    uri: httpUrl,
    credentials: 'include',
  });

  /**
   * Create WebSocket link for subscriptions
   * IMPORTANT: connectionParams is called on each connection, ensuring session ID is always fresh
   */
  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUrl,
      connectionParams: () => {
        const sessionId = getSessionId();
        console.log('[WebChatWidget] WebSocket connecting with session ID:', sessionId, 'company:', companyPublicId);
        return {
          sessionId,
          'x-session-id': sessionId,
          companyPublicId,
          'x-company-id': companyPublicId,
        };
      },
      keepAlive: 10000,
      retryAttempts: 5,
      shouldRetry: () => true,
      lazy: true,
      on: {
        connected: () => {
          const currentSessionId = getSessionId();
          console.log('[WebChatWidget] WebSocket connected with session:', currentSessionId);
        },
        closed: (event) => {
          console.log('[WebChatWidget] WebSocket connection closed:', event);
        },
        error: (error) => {
          console.error('[WebChatWidget] WebSocket connection error:', error);
        },
      },
    })
  );

  /**
   * Add session ID and company ID to headers for HTTP requests
   */
  const authLink = new ApolloLink((operation, forward) => {
    const sessionId = getSessionId();

    operation.setContext({
      headers: {
        'x-session-id': sessionId,
        'x-company-id': companyPublicId,
      },
    });

    return forward(operation);
  });

  /**
   * Split between WebSocket and HTTP links
   */
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    authLink.concat(httpLink)
  );

  /**
   * Apollo Client instance
   */
  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            messages: {
              keyArgs: false,
              merge(existing = { edges: [], pageInfo: {} }, incoming, { args }) {
                // If this is a pagination request (has 'before' cursor), merge with existing
                // Otherwise, replace existing data (initial load or refetch)
                const isPagination = args?.input?.before;

                if (isPagination) {
                  // Pagination: prepend older messages
                  return {
                    ...incoming,
                    edges: [...incoming.edges, ...existing.edges],
                  };
                } else {
                  // Initial load or refetch: replace data
                  return incoming;
                }
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });
}
